// src/pages/fleet/FleetAiPanel.tsx — the dashboard assistant.
//
// Sits in the empty column beside the charts and answers questions about the
// fleet's own data: which trips are running, which truck went off route, how far
// behind schedule something is. The server builds the data snapshot and the model
// is told to answer only from it, so this panel never invents a figure.
//
// It is read-only by design. Asked to start or cancel a trip, it says which
// screen does that instead — a chat box is the wrong place to dispatch a truck.
import React, { useEffect, useRef, useState } from 'react';
import { askFleetAi } from '../../services/fleetApi';
import type { AiTurn } from '../../services/fleetApi';
import { apiError } from '../../utils/errors';
import { css } from '../../utils/css';

/** Openers that show what the panel is for, rather than a blank box. */
const SUGGESTIONS = [
  'How many trips are running now?',
  'Which truck went off route?',
  'Which trips are running late?',
  'What is pending with IFMS?',
];

export default function FleetAiPanel() {
  const [turns, setTurns] = useState<AiTurn[]>([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [offline, setOffline] = useState(false);
  const [offlineNote, setOfflineNote] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns, busy]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || busy) return;
    setErr(''); setQ(''); setBusy(true);
    const history = turns;
    setTurns((t) => [...t, { role: 'user', content: question }]);
    try {
      const r = await askFleetAi(question, history);
      setOffline(r.data.mode === 'offline');
      setOfflineNote(r.data.mode === 'offline' ? (r.data.note || '') : '');
      setTurns((t) => [...t, { role: 'assistant', content: r.data.note ? `${r.data.note}\n\n${r.data.answer}` : r.data.answer }]);
    } catch (e) {
      setErr(apiError(e, 'Could not reach the assistant'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={S.card}>
      <div style={S.head}>
        <div>
          <div style={S.title}>🤖 Ask about the fleet</div>
          <div style={S.sub}>Answers come from this system's own trips, trucks and alerts</div>
        </div>
        {turns.length > 0 && (
          <button type="button" style={S.clear} onClick={() => { setTurns([]); setErr(''); }}>Clear</button>
        )}
      </div>

      <div style={S.body}>
        {turns.length === 0 && !busy && (
          <div style={S.empty}>
            {/* The assistant replies in whatever language it is asked in; the
                interface itself stays English, like the rest of the portal. */}
            <div style={S.emptyText}>Ask in English, Hindi or Hinglish — it replies in the same language.</div>
            <div style={S.chips}>
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" style={S.chip} onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} style={t.role === 'user' ? S.rowUser : S.rowBot}>
            <div style={t.role === 'user' ? S.bubbleUser : S.bubbleBot}>{t.content}</div>
          </div>
        ))}

        {busy && <div style={S.rowBot}><div style={{ ...S.bubbleBot, ...S.thinking }}>Thinking…</div></div>}
        {err && <div style={S.err}>{err}</div>}
        <div ref={endRef} />
      </div>

      {offline && (
        <div style={S.offline}>
          {/* "No key" and "the key failed" need different fixes, so the server
              says which one it is and the banner repeats that, not a guess. */}
          {offlineNote || (
            <>
              No AI key is set on this server, so the panel is answering with a plain data summary.
              Add <code style={S.code}>OPENAI_API_KEY</code> or <code style={S.code}>ANTHROPIC_API_KEY</code> to{' '}
              <code style={S.code}>backend/.env</code> for full answers.
            </>
          )}
        </div>
      )}

      <form
        style={S.foot}
        onSubmit={(e) => { e.preventDefault(); send(q); }}
      >
        <input
          style={S.input}
          value={q}
          maxLength={600}
          placeholder="Type your question…"
          onChange={(e) => setQ(e.target.value)}
          disabled={busy}
        />
        <button type="submit" style={{ ...S.send, ...(busy || !q.trim() ? S.sendOff : {}) }} disabled={busy || !q.trim()}>
          Ask
        </button>
      </form>
    </section>
  );
}

const S = css({
  card: {
    background: 'var(--fx-surface)', borderRadius: 14, boxShadow: 'var(--fx-shadow)',
    display: 'flex', flexDirection: 'column', minHeight: 420, maxHeight: 620, overflow: 'hidden',
  },
  head: { padding: '13px 16px', borderBottom: '1px solid var(--fx-border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 },
  title: { fontSize: 14, fontWeight: 700, color: 'var(--fx-text)' },
  sub: { fontSize: 11.5, color: 'var(--fx-text-2)', marginTop: 2 },
  clear: { background: 'transparent', border: 'none', color: 'var(--fx-text-3)', fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', padding: 2 },

  body: { flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { margin: 'auto 0', textAlign: 'center' },
  emptyText: { fontSize: 12.5, color: 'var(--fx-text-2)', marginBottom: 12 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center' },
  chip: {
    background: 'var(--fx-gray-soft)', border: '1px solid var(--fx-border)', borderRadius: 20,
    padding: '6px 12px', fontSize: 11.5, color: 'var(--fx-text-2)', cursor: 'pointer', fontFamily: 'inherit',
  },

  rowUser: { display: 'flex', justifyContent: 'flex-end' },
  rowBot: { display: 'flex', justifyContent: 'flex-start' },
  bubbleUser: {
    background: 'var(--fx-accent)', color: '#fff', borderRadius: '12px 12px 3px 12px',
    padding: '8px 12px', fontSize: 12.5, lineHeight: 1.5, maxWidth: '85%', whiteSpace: 'pre-wrap',
  },
  bubbleBot: {
    background: 'var(--fx-gray-soft)', color: 'var(--fx-text)', borderRadius: '12px 12px 12px 3px',
    padding: '9px 12px', fontSize: 12.5, lineHeight: 1.6, maxWidth: '92%', whiteSpace: 'pre-wrap',
  },
  thinking: { color: 'var(--fx-text-3)', fontStyle: 'italic' },
  err: { fontSize: 12, color: 'var(--fx-red)', background: 'var(--fx-red-soft)', borderRadius: 9, padding: '8px 11px' },

  offline: { fontSize: 11, lineHeight: 1.5, color: 'var(--fx-orange)', background: 'var(--fx-orange-soft)', padding: '8px 16px', borderTop: '1px solid var(--fx-border)' },
  code: { fontFamily: 'DM Mono, monospace', fontSize: 10.5 },

  foot: { display: 'flex', gap: 8, padding: '11px 14px', borderTop: '1px solid var(--fx-border)' },
  input: {
    flex: 1, padding: '9px 12px', borderRadius: 10, border: '1px solid var(--fx-border-strong)',
    background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 12.5, outline: 'none', fontFamily: 'inherit',
  },
  send: {
    background: 'var(--fx-accent)', color: '#fff', border: 'none', borderRadius: 10,
    padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  sendOff: { opacity: 0.45, cursor: 'default' },
});
